import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { FetchBackend, HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { finalize, Observable, of, shareReplay, tap } from 'rxjs';

import { CacheService } from './cache.service';
import { SKIP_API_CACHE } from './http-context';

/**
 * `FetchBackend` *with* an SSR response cache, installed as the `HttpBackend`
 * — i.e. *below* Angular's transfer-cache root interceptor — on purpose.
 *
 * As an interceptor (its previous form) it pre-empted `transferCacheInterceptorFn`:
 * on a warm SSR hit it returned the cached response without ever reaching the
 * transfer cache, so nothing was serialized into `ng-state` and the browser
 * re-fetched every GET after hydration. Sitting at the backend, a warm hit's
 * response still bubbles up through the transfer-cache interceptor and gets
 * serialized as designed — client no longer re-fetches, backend still shielded.
 *
 * It *extends* `FetchBackend` (rather than wrapping it) so Angular's
 * server-side `instanceof FetchBackend` check still passes — wrapping tripped a
 * false NG02801 "configure fetch" warning even though fetch was in use.
 *
 * Browser: pure passthrough. Server: process-global GET cache + in-flight
 * coalescing via the shared {@link CacheService} (unchanged).
 */
@Injectable()
export class SsrCacheBackend extends FetchBackend {
    private platformId = inject(PLATFORM_ID);
    private cacheService = inject(CacheService);

    override handle(request: HttpRequest<unknown>): Observable<HttpEvent<unknown>> {
        if (isPlatformBrowser(this.platformId) || request.method !== 'GET' || request.context.get(SKIP_API_CACHE)) {
            return super.handle(request);
        }

        // ApiPrefix already rewrote the URL and ApiAcceptLanguage already set the
        // header (both run as interceptors above): keying on Accept-Language
        // prevents serving a pl render to a de visitor (and v.v.).
        const key = `${request.urlWithParams}\n${request.headers.get('Accept-Language') ?? ''}`;

        const cachedResponse = this.cacheService.get(key);

        if (cachedResponse) {
            return of(cachedResponse);
        }

        // A render for the same key is already waiting on the backend — share it
        // instead of firing a duplicate request (cache-stampede protection).
        const inFlight = this.cacheService.getInFlight(key);

        if (inFlight) {
            return inFlight;
        }

        const request$ = super.handle(request).pipe(
            tap((event) => {
                if (event instanceof HttpResponse && event.status === 200) {
                    this.cacheService.set(key, event);
                }
            }),
            finalize(() => this.cacheService.clearInFlight(key)),
            shareReplay(1),
        );

        this.cacheService.setInFlight(key, request$);

        return request$;
    }
}
